import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Select, Textarea } from '@artosera/ui';
import {
  ETATS_CAISSE,
  TYPES_CAISSE,
  type EtatCaisse,
  type TypeCaisse,
  type DimensionsCaisse as Dimensions,
} from '@artosera/shared';
import {
  reserveApi,
  useEmplacements,
  aplatirEmplacements,
  useMutationReserve,
  type CibleEtiquette,
  type FicheCaisse,
} from '../../api/reserve.js';
import { appelDeRequete, cleMetier, dansLaPorteeCourante } from '../../api/cles.js';
import { useLieux } from '../../api/lieux.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { Scanner } from './Scanner.js';
import { Destination } from './Destination.js';
import { DimensionsCaisse, dimensionsCompletes, dimensionsValides } from './DimensionsCaisse.js';
import { Etiquettes } from './Etiquettes.js';
import styles from './InventairePage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

function ModifierCaisse({ caisse, fermer }: { caisse: FicheCaisse; fermer: () => void }) {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const [type, setType] = useState(caisse.type);
  const [etat, setEtat] = useState(caisse.etat);
  const [notes, setNotes] = useState(caisse.notes);
  const [interieur, setInterieur] = useState<Partial<Dimensions>>(
    caisse.dimensionsInterieures ?? {},
  );
  const [exterieur, setExterieur] = useState<Partial<Dimensions>>(
    caisse.dimensionsExterieures ?? {},
  );
  const modifier = useMutationReserve(galerieSlug, (_: void, appel) =>
    reserveApi.modifierCaisse(
      galerieSlug,
      caisse.id,
      {
        revision: caisse.revision,
        type,
        etat,
        notes,
        dimensionsInterieures: dimensionsCompletes(interieur),
        dimensionsExterieures: dimensionsCompletes(exterieur),
      },
      appel,
    ),
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) fermer();
      }}
      title={t('regie:inventaire.modifier')}
      closeLabel={t('commun:actions.fermer')}
    >
      <div className={styles.formulaire}>
        <Field htmlFor="caisse-type" label={t('regie:inventaire.type')}>
          <Select
            id="caisse-type"
            value={type}
            onValueChange={(v) => setType(v as TypeCaisse)}
            options={TYPES_CAISSE.map((v) => ({
              value: v,
              label: t(`regie:inventaire.typesCaisse.${v}`),
            }))}
          />
        </Field>
        <Field htmlFor="caisse-etat" label={t('regie:inventaire.etat')}>
          <Select
            id="caisse-etat"
            value={etat}
            onValueChange={(v) => setEtat(v as EtatCaisse)}
            options={ETATS_CAISSE.map((v) => ({
              value: v,
              label: t(`regie:inventaire.etatsCaisse.${v}`),
            }))}
          />
        </Field>
        <DimensionsCaisse
          titre={t('regie:inventaire.interieur')}
          valeur={interieur}
          onChange={setInterieur}
        />
        <DimensionsCaisse
          titre={t('regie:inventaire.exterieur')}
          valeur={exterieur}
          onChange={setExterieur}
        />
        <Field htmlFor="caisse-notes" label={t('regie:inventaire.note')}>
          <Textarea
            id="caisse-notes"
            maxLength={5000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <Button
          disabled={
            modifier.isPending || !dimensionsValides(interieur) || !dimensionsValides(exterieur)
          }
          onClick={() => modifier.mutate(undefined, { onSuccess: fermer })}
        >
          {t('regie:inventaire.confirmer')}
        </Button>
        {modifier.error && (
          <p role="alert">
            {estApiError(modifier.error) ? modifier.error.message : t('commun:erreurReseau')}
          </p>
        )}
      </div>
    </Dialog>
  );
}
export function CaisseDetailPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const { id = '' } = useParams();
  const lieux = useLieux(galerieSlug, true);
  const emplacements = useEmplacements(galerieSlug, true);
  const fiche = useQuery({
    queryKey: cleMetier(galerieSlug, 'reserve', 'caisse', id),
    queryFn: (c) => reserveApi.caisse(galerieSlug, id, appelDeRequete(c)),
  });
  const caisse = fiche.data?.caisse;
  const [lieuId, setLieuId] = useState('');
  const [emplacementId, setEmplacementId] = useState('');
  const [modifier, setModifier] = useState(false);
  const [retirer, setRetirer] = useState<string | null>(null);
  const [ajout, setAjout] = useState<{
    cible: CibleEtiquette;
    oeuvreId: string;
    titre: string;
  } | null>(null);
  const [erreur, setErreur] = useState('');
  const mouvement = useMutationReserve(galerieSlug, (revision: number, appel) =>
    reserveApi.deplacerCaisse(
      galerieSlug,
      id,
      { lieuId, emplacementId: emplacementId || null, revision },
      appel,
    ),
  );
  const sortie = useMutationReserve(
    galerieSlug,
    (corps: { cle: string; revision: number }, appel) =>
      reserveApi.retirerContenu(galerieSlug, id, corps.cle, corps.revision, appel),
  );
  const entree = useMutationReserve(
    galerieSlug,
    (corps: { oeuvreId: string; exemplaireId: string | null; revision: number }, appel) =>
      reserveApi.ajouterContenu(galerieSlug, id, corps, appel),
  );
  async function scanner(code: string) {
    setErreur('');
    try {
      const resolution = await dansLaPorteeCourante((appel) =>
        reserveApi.resoudre(galerieSlug, code, appel),
      );
      if (!('titre' in resolution.resume)) {
        setErreur(t('regie:inventaire.oeuvreRequise'));
        return;
      }
      setAjout({
        cible: resolution.cible,
        oeuvreId: resolution.resume.oeuvreId,
        titre: resolution.resume.titre,
      });
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  const erreurMutation = entree.error ?? mouvement.error ?? sortie.error;
  const ecrit = galerieALaction(galerie, 'inventaire:write');
  if (!caisse)
    return (
      <p role={fiche.isError ? 'alert' : 'status'}>
        {t(fiche.isError ? 'commun:erreurReseau' : 'commun:chargement')}
      </p>
    );
  return (
    <section className={styles.page} data-ui="page">
      <Link className={styles.lien} to={`/${galerieSlug}/inventaire?onglet=caisses`}>
        {t('regie:inventaire.caisses')}
      </Link>
      <header className={styles.entete} data-ui="page-heading">
        <h1>{caisse.numero}</h1>
        <p>{t(`regie:inventaire.etatsCaisse.${caisse.etat}`)}</p>
        <Verrouillable action="inventaire:write" ouvert={ecrit}>
          <Button onClick={() => setModifier(true)}>{t('regie:inventaire.modifier')}</Button>
        </Verrouillable>
      </header>
      <p>
        {t(`regie:inventaire.typesCaisse.${caisse.type}`)} · {caisse.notes}
      </p>
      {(['dimensionsInterieures', 'dimensionsExterieures'] as const).map((cle) => (
        <p key={cle}>
          {t(
            cle === 'dimensionsInterieures'
              ? 'regie:inventaire.interieur'
              : 'regie:inventaire.exterieur',
          )}{' '}
          :{' '}
          {caisse[cle]
            ? `${caisse[cle].hauteur} × ${caisse[cle].largeur} × ${caisse[cle].profondeur} cm`
            : '—'}
        </p>
      ))}
      <p>
        {t('regie:inventaire.lieu')} : {lieux.data?.find((l) => l.id === caisse.lieuId)?.nom ?? '—'}{' '}
        ·{' '}
        {aplatirEmplacements(emplacements.data?.emplacements ?? []).find(
          (e) => e.id === caisse.emplacementId,
        )?.chemin ?? '—'}
      </p>
      <Etiquettes cibles={[{ type: 'caisse', id }]} />
      <section className={styles.panneau}>
        <h2>{t('regie:inventaire.contenu')}</h2>
        <ul className={styles.arbre}>
          {caisse.contenu.map((l) => (
            <li className={styles.ligne} key={l.cle}>
              <div>
                <Link className={styles.lien} to={`/${galerieSlug}/oeuvres/${l.oeuvreId}`}>
                  {l.titre} · {l.numeroInventaire} {l.numeroExemplaire}
                </Link>
                <p>{l.artisteNom}</p>
                {l.dimensions && (
                  <p>
                    {l.dimensions.hauteur} × {l.dimensions.largeur} × {l.dimensions.profondeur ?? 0}{' '}
                    cm
                  </p>
                )}
                {l.nentrePas && <p className={styles.erreur}>{t('regie:inventaire.nentrePas')}</p>}
              </div>
              {ecrit && (
                <Button variant="ghost" onClick={() => setRetirer(l.cle)}>
                  {t('regie:inventaire.retirer')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>
      {ecrit && caisse.etat !== 'reformee' && (
        <>
          <section className={styles.panneau}>
            <h2>{t('regie:inventaire.ajouterContenu')}</h2>
            <Scanner onScan={scanner} />
            <p>{ajout?.titre}</p>
            <Button
              disabled={!ajout || entree.isPending}
              onClick={() => {
                if (ajout)
                  entree.mutate(
                    {
                      oeuvreId: ajout.oeuvreId,
                      exemplaireId: ajout.cible.type === 'exemplaire' ? ajout.cible.id : null,
                      revision: caisse.revision,
                    },
                    { onSuccess: () => setAjout(null) },
                  );
              }}
            >
              {t('regie:inventaire.confirmer')}
            </Button>
          </section>
          <section className={styles.panneau}>
            <h2>{t('regie:inventaire.deplacerCaisse')}</h2>
            <p>{t('regie:inventaire.contenuSuit')}</p>
            <Destination
              galerie={galerieSlug}
              lieuId={lieuId}
              emplacementId={emplacementId}
              onChange={(lieu, emplacement) => {
                setLieuId(lieu);
                setEmplacementId(emplacement);
              }}
            />
            <Button
              disabled={!lieuId || mouvement.isPending}
              onClick={() => mouvement.mutate(caisse.revision)}
            >
              {t('regie:inventaire.confirmer')}
            </Button>
          </section>
        </>
      )}
      {(erreur || entree.error || mouvement.error || sortie.error) && (
        <p role="alert">
          {erreur ||
            (estApiError(erreurMutation) ? erreurMutation.message : t('commun:erreurReseau'))}
        </p>
      )}
      {modifier && <ModifierCaisse caisse={caisse} fermer={() => setModifier(false)} />}
      <Dialog
        open={retirer !== null}
        onOpenChange={(v) => {
          if (!v) setRetirer(null);
        }}
        title={t('regie:inventaire.retirer')}
        description={t('regie:inventaire.retraitConfirmation')}
        closeLabel={t('commun:actions.fermer')}
      >
        <Button
          disabled={sortie.isPending}
          onClick={() => {
            if (retirer)
              sortie.mutate(
                { cle: retirer, revision: caisse.revision },
                { onSuccess: () => setRetirer(null) },
              );
          }}
        >
          {t('regie:inventaire.confirmer')}
        </Button>
        {sortie.error && (
          <p role="alert">
            {estApiError(sortie.error) ? sortie.error.message : t('commun:erreurReseau')}
          </p>
        )}
      </Dialog>
    </section>
  );
}
